/**
 * Federated search source adapters for the Search page (/search).
 *
 * Each source is plain data + normalize functions (no classes, no registry):
 *   { type, label, chip, load(ctx), normalize(raw) }
 *
 * Adapter contract (load-bearing): `load()` validates its result and THROWS on
 * failure — `null`, `success === false`, or non-array (after `.data` unwrap).
 * `API.*` methods return error envelopes or `null` instead of throwing, while
 * `discoveryApi._request` throws; normalizing all failure shapes to a rejected
 * promise makes `Promise.allSettled` rejection the single failure signal the
 * Search page branches on.
 *
 * SearchItem shape (identity across the page is the (type, id) PAIR — ids can
 * collide across sources):
 *   {
 *     type,         // 'session' | 'note' | 'guide'
 *     id,           // source row id (session-note id / memo id / guideDefinitionId)
 *     title,        // card title
 *     subtitle,     // default second line when no query is active
 *     date,         // ISO string; drives sort + month grouping (null-date items
 *                   //   are dropped from the stream by the Search page)
 *     starred,      // bool — star marker + star-filter state
 *     starredAt,    // ISO string | null — when the item was starred (sort key
 *                   //   for the sidebar's starred block); null when unstarred
 *     archived,     // bool — Active/Archived view state
 *     archivedAt,   // ISO string | null — when the item was archived; null when active
 *     viewed,       // bool — unread state for sidebar recents rows
 *                   //   (note/guide/practice — sessions read `flagged`
 *                   //   instead, see below; sent notes are always excluded from
 *                   //   Recents so `viewed` is inert-but-harmless there; guides
 *                   //   use freshness semantics — see normalizeGuide)
 *     flagged,      // bool — session-only manual unread toggle
 *                   //   (SessionNote.flaggedAt). NOT persisted viewed-state — a chat is
 *                   //   never "new" to the person who was in it, so sessions
 *                   //   don't use the automatic viewed-driven dot other types
 *                   //   do. Presented with the shared Mark as read/unread UI;
 *                   //   undefined
 *                   //   for note/guide items.
 *     searchFields, // string[] — ordered, title first, then subtitle, then deep fields
 *     payload       // raw item, used by open behavior + type-specific logic
 *   }
 *
 * Guide identity is ALWAYS `guideDefinitionId` (the content-registry slug) —
 * never the `UserGuide` row UUID. This source is built from
 * `API.getAvailableGuides()` (the `getAvailableGuides()`-shaped summary,
 * where `.id` genuinely IS the guideDefinitionId), NOT a raw `GET /guides`
 * list row (whose `.id` is the row UUID and has no `cardContent`).
 *
 * Not every export here is federated into the `searchSources` array below.
 * `PRACTICE_SOURCE` and `isRecentsEligible` are exported for two OTHER
 * consumers — `SidebarRecentsController` and `DashboardManager`'s Recent/
 * Starred views — that both need a practice adapter and the same recency-
 * eligibility rule, but `searchSources` itself must stay exactly the /search
 * page's federated set (session/note/guide); adding practices to it would
 * change what /search shows.
 *
 * @module components/searchSources
 */

import { API } from '../core/api.js';
import { DiscoveryAPI } from '../core/discoveryApi.js';
import { isNewSinceViewed } from '../core/utils.js';
import { noteCardTitle } from './partnerNotesDrillHelper.js';

/**
 * Per-type card icon (sprite id) — shared by the /search stream and the
 * sidebar recents rows. `thread` has no adapter here (open threads
 * are a sidebar-only source — see SidebarRecentsController) but still needs
 * an entry so its rows can share this same icon lookup. `practice` also has
 * no adapter here (practices are a sidebar-only source, like `thread`) but
 * still needs an entry so its rows can share this lookup too. The dead
 * `library` placeholder type is retired (see the guide source below).
 *
 * `guide` is the one type whose slot can carry a picture instead: on /search
 * and the dashboard's Recommended and library rows, a guide with path artwork
 * renders `guideCoverHtml` (the small cover page — `renderGuideCover` below)
 * and falls back to the book glyph below only when it has none. The cover
 * says WHICH path's guide a row is, which the book cannot, and its
 * upright-page silhouette says "guide" where the path's own circular
 * `.path-disc` would make a guide row read as the path that produced it. The
 * sidebar recents is the exception and draws the book for every guide: a
 * typed, monochrome list at 20px whose guide titles already name the path.
 */
export const TYPE_ICON = {
  session: 'chat-bubble',
  note: 'email',
  guide: 'guide',
  thread: 'question-mark',
  practice: 'practices'
};

/**
 * Per-type rendered icon size (px) — tuned per glyph so each reads at a
 * consistent visual weight against the sprite. Defaults to 21.
 *
 * Only `practice` deviates, for both of its glyphs: the checkbox is drawn on
 * a 25x27.86 Inkscape grid whose non-square viewBox costs ~10% of the scale,
 * and the lightbulb's bulb-plus-rays artwork is weighted (sprite.svg) to
 * match that checkbox at the same 25, so they read as one pair on a
 * /experiments row. Every icon drawn on the shared 24-grid (email,
 * question-mark, guide) stays at 21, where a 1.6 stroke lands at 1.40px
 * apparent — do not add an override for those.
 */
export const TYPE_ICON_SIZE = {
  note: 21,
  thread: 21,
  practice: 25
};

/**
 * The small cover-page tile a guide row leads with on the dashboard's
 * Recommended and library rows and on /search: a portrait sheet
 * whose top is the backing path's artwork edge to edge, with a white foot
 * carrying one rule. The page says "document"; the picture says which path
 * the document came from — distinct from the path's own circular `.path-disc`,
 * so a guide and the path that produced it never wear the same shape. A guide
 * with no artwork renders nothing here and the caller falls back to the book
 * glyph, a document without a path. The host sizes it through `--cover-size`
 * on the cover or any ancestor (components/guide-cover.css). The /library
 * Guides tab draws its own larger tile (`InsightsSection.renderGuideTile`).
 *
 * @param {string|null|undefined} image - `cardContent.image` filename
 * @returns {string} HTML string, or ''
 */
export function renderGuideCover(image) {
  if (!image) return '';
  return `<span class="guide-cover" aria-hidden="true"><span class="guide-cover__art"><img src="assets/images/paths/${encodeURIComponent(image)}" alt=""></span><span class="guide-cover__foot"><span></span></span></span>`;
}

/**
 * `renderGuideCover` for one SearchItem: the cover for a guide with artwork,
 * '' for a guide without (a Together-only guide) or any other type, in which
 * case the caller renders `iconIdForItem`'s glyph.
 *
 * @param {Object} item - SearchItem
 * @returns {string} HTML string, or ''
 */
export function guideCoverHtml(item) {
  return item?.type === 'guide' ? renderGuideCover(item.payload?.image) : '';
}

/**
 * Sprite id (without the `icon-` prefix) for one SearchItem's leading slot.
 *
 * A completed experiment is a different object from a running one — a
 * finding, something learned — and takes the lightbulb wherever it appears as
 * a row (/experiments Findings, sidebar recents, /search). Running and
 * archived experiments keep the checkbox, as does the Experiments nav entry.
 * @param {{type: string, payload?: {status?: string}}} item
 * @returns {string}
 */
export function iconIdForItem(item) {
  if (item?.type === 'practice' && item.payload?.status === 'completed') return 'lightbulb';
  return TYPE_ICON[item?.type] || 'document';
}

/**
 * Rendered size (px) for `iconIdForItem`'s glyph — per item type, so a
 * Finding's lightbulb and a running experiment's checkbox share the practice
 * size (see TYPE_ICON_SIZE).
 * @param {{type: string, payload?: {status?: string}}} item
 * @returns {number}
 */
export function iconSizeForItem(item) {
  return TYPE_ICON_SIZE[item?.type] || 21;
}

// The Search page controller (SessionsSection) doesn't own a DiscoveryAPI
// instance, so the notes adapter carries its own. Exported so SessionsSection's
// note-action fan-out (star/archive/delete memos) reuses the SAME instance the
// page loaded notes with — one DiscoveryAPI per page.
export const discoveryApi = new DiscoveryAPI();

/**
 * Validate a list response and unwrap it to a plain array — throws on any
 * failure shape (see adapter contract above).
 * @param {*} response - Raw API response (array, envelope, or null)
 * @param {string} sourceLabel - Source name for the error message
 * @returns {Array} Unwrapped array
 */
function assertDataArray(response, sourceLabel) {
  if (!response) {
    throw new Error(`[searchSources] ${sourceLabel}: no response`);
  }
  if (response.success === false) {
    throw new Error(response.error || `[searchSources] ${sourceLabel}: request failed`);
  }
  const arr = Array.isArray(response) ? response : response.data;
  if (!Array.isArray(arr)) {
    throw new Error(`[searchSources] ${sourceLabel}: unexpected payload shape`);
  }
  return arr;
}

/**
 * Stamp continuation notes with their thread anchor title (`_continuedFromTitle`).
 *
 * Runs once at load time against the FULL notes list so the card renderer and
 * the search index consume the same resolved value (never resolve against the
 * filtered array). The anchor is the deepest reachable ancestor's title — the
 * chain root when fully loaded, matching what that ancestor's own card shows.
 * Unresolvable cases (parent deleted / private — no note row / light-classified /
 * beyond the loaded page, null or empty anchor title, dangling or cyclic ids)
 * leave the note unstamped: the card falls back to its own title, no prefix.
 *
 * @param {Object[]} notes - Loaded session notes (mutated in place)
 */
export function stampContinuationTitles(notes) {
  const bySessionId = new Map();
  for (const note of notes) {
    const sid = note.session?.id || note.sessionId;
    if (sid) bySessionId.set(sid, note);
  }

  for (const note of notes) {
    delete note._continuedFromTitle;
    const entry = note.session?.entry;
    if (entry?.entryType !== 'session_continuation' || !entry.sourceSessionId) continue;

    const visited = new Set([note.session?.id || note.sessionId]);
    let currentId = entry.sourceSessionId;
    let anchor = null;
    let hops = 0;
    while (currentId && !visited.has(currentId) && hops < 10) {
      visited.add(currentId);
      hops += 1;
      const parent = bySessionId.get(currentId);
      if (!parent) break;
      anchor = parent;
      currentId = parent.session?.entry?.entryType === 'session_continuation'
        ? parent.session.entry.sourceSessionId
        : null;
    }

    const anchorTitle = anchor?.notesJson?.title;
    if (typeof anchorTitle === 'string' && anchorTitle.trim()) {
      note._continuedFromTitle = anchorTitle.trim();
    }
  }
}

/**
 * Normalize a raw session note into a SearchItem.
 * Also used by SessionsSection.renderSessionCard to keep its external
 * note-shaped signature while delegating to the SearchItem renderer.
 * @param {Object} note - Session note from API (optionally stamped with `_continuedFromTitle`)
 * @returns {Object} SearchItem
 */
export function normalizeSessionNote(note) {
  const notesJson = note.notesJson || {};
  const continuedFrom = typeof note._continuedFromTitle === 'string' ? note._continuedFromTitle : null;
  return {
    type: 'session',
    id: note.id,
    title: continuedFrom || notesJson.title || 'Session Notes',
    subtitle: notesJson.summary?.text || '',
    date: note.session?.endedAt || note.createdAt || null,
    // Content-actions state (search select-mode): star marker + archive view.
    starred: Boolean(note.starredAt),
    starredAt: note.starredAt || null,
    archived: Boolean(note.archivedAt),
    archivedAt: note.archivedAt || null,
    viewed: Boolean(note.viewedAt),
    // Manual-only unread toggle — replaces automatic viewed state for
    // sessions specifically (see SearchItem shape doc).
    flagged: Boolean(note.flaggedAt),
    searchFields: [
      notesJson.title || '',
      note._continuedFromTitle || '',
      notesJson.summary?.text || '',
      notesJson.summary?.keyTakeaway || '',
      ...(notesJson.observations || []).map(o => `${o.title || ''} ${o.description || ''}`),
      ...(notesJson.practices || []).map(p => p.action || ''),
      ...(notesJson.openThreads || []).map(q => q.question || ''),
      ...(note.themes || [])
    ],
    payload: note
  };
}

/**
 * Normalize a raw partner memo into a SearchItem.
 * @param {Object} memo - Partner memo from the received or sent list
 * @param {'received'|'sent'} direction - Which list the memo came from
 * @param {string} partnerName - Display name of the linked partner
 * @returns {Object} SearchItem
 */
export function normalizeMemo(memo, direction, partnerName) {
  const content = memo.content || '';
  // Card title is the note's own (agent-authored or prose-derived) title — the
  // same resolver the detail panel + /notes rows use (shared, DRY).
  const title = noteCardTitle(memo);
  // Keep the "Note from/to {partner}" phrasing searchable even though it's no
  // longer the visible title.
  const directionLabel = direction === 'sent'
    ? `Note to ${partnerName}`
    : `Note from ${partnerName}`;
  return {
    type: 'note',
    id: memo.id,
    title,
    subtitle: content.length > 140 ? `${content.slice(0, 140)}…` : content,
    date: memo.sentAt || memo.createdAt || null,
    // Received notes carry a recipient-side star; archive is per-side (unified).
    starred: Boolean(memo.starredAt),
    starredAt: memo.starredAt || null,
    archived: Boolean(memo.archivedAt),
    archivedAt: memo.archivedAt || null,
    // Received-note viewed state (sent notes never render in Recents at all,
    // so this is inert-but-harmless there — see SidebarRecentsController).
    viewed: Boolean(memo.viewedAt),
    searchFields: [title, directionLabel, content],
    // `direction` + `partnerName` ride on the payload so the /search note-detail
    // panel is fully self-contained (renders received/sent + the partner name
    // without a second partner fetch — see partnerNotesDrillHelper).
    payload: { ...memo, direction, partnerName }
  };
}

/**
 * Normalize a `getAvailableGuides()`-shaped guide summary into a SearchItem.
 * `.id` on this shape genuinely IS the guideDefinitionId (the content-registry
 * slug) — this is NOT a raw `UserGuide` row (where `.id` is the row UUID and
 * `.guideDefinitionId` is the separate slug field). Never build this from a
 * raw `API.getGuide()` result.
 * @param {Object} guide - Entry from `API.getAvailableGuides()`
 * @returns {Object} SearchItem
 */
export function normalizeGuide(guide) {
  const cardContent = guide.cardContent || guide.pathMetadata?.cardContent || {};
  return {
    type: 'guide',
    id: guide.id,
    title: guide.title,
    subtitle: guide.subtitle || '',
    date: guide.generatedAt || guide.createdAt || null,
    starred: Boolean(guide.starredAt),
    starredAt: guide.starredAt || null,
    archived: Boolean(guide.archivedAt),
    archivedAt: guide.archivedAt || null,
    // Freshness semantics, not naive Boolean(viewedAt): regenerated content
    // becomes unviewed again, using the same helper as BadgeService.
    viewed: !isNewSinceViewed(guide.generatedAt || guide.createdAt, guide.viewedAt),
    searchFields: [guide.title || '', guide.subtitle || ''],
    payload: {
      image: cardContent.image || null,
      color: cardContent.color || null,
      guideDefinitionId: guide.id
    }
  };
}

/**
 * Normalize a raw Practice row into a SearchItem-shaped row. `archived`
 * mirrors `status === 'archived'` for the shared Archive/Restore action
 * machinery and for `isRecentsEligible` below (which additionally requires
 * `status === 'active'`, so a completed practice is neither "recent" nor
 * "archived" — it just needs a star to surface anywhere).
 * @param {Object} practice - Practice row from `API.getPractices`
 * @returns {Object} SearchItem
 */
function normalizePractice(practice) {
  return {
    type: 'practice',
    id: practice.id,
    title: practice.title,
    date: practice.createdAt,
    starred: Boolean(practice.starredAt),
    starredAt: practice.starredAt || null,
    archived: practice.status === 'archived',
    archivedAt: null,
    viewed: Boolean(practice.viewedAt),
    payload: practice
  };
}

/**
 * Practices source — NOT part of the federated `searchSources` array below
 * (practices have no presence on /search). Loads the full non-draft set
 * (`status: 'all'`) and normalizes everything through; callers narrow by
 * `starred`/`archived`/`payload.status` as needed (mirrors
 * `SidebarRecentsController._computeItems`'s split).
 */
export const PRACTICE_SOURCE = {
  type: 'practice',
  label: 'practices',
  async load() {
    const data = await API.getPractices({ status: 'all', limit: 100 });
    if (!Array.isArray(data)) {
      throw new Error('[searchSources] practices: unexpected payload shape');
    }
    return data;
  },
  normalize(practices) {
    return practices.map(normalizePractice);
  }
};

/**
 * Whether an item belongs in a "recency" surface: sessions + received notes +
 * guides + in-progress (active) practices, excluding archived. Shared by
 * `SidebarRecentsController`'s recency block and `DashboardManager`'s Recent
 * view — both apply the same eligibility rule to the same four content types.
 *
 * (`/search` itself does NOT hide archived items — they stay inline,
 * de-emphasized — so this is `/search`'s source with an additional archived
 * filter, not the same set.) Sent notes are excluded entirely — mirrors the
 * `isSentNote` Discuss-omission check in `SidebarRecentsController`. Threads
 * never qualify (not a valid `type` here at all — `THREAD_SOURCE` stays
 * sidebar-private, never reaches this function). Practices qualify ONLY when
 * `status === 'active'` — a completed or archived practice needs a star to
 * appear in a recency surface.
 * @param {Object} item - SearchItem
 * @returns {boolean}
 */
export function isRecentsEligible(item) {
  return ['session', 'note', 'guide', 'practice'].includes(item.type)
    && !item.archived
    && (item.type !== 'note' || item.payload?.direction !== 'sent')
    && (item.type !== 'practice' || item.payload?.status === 'active');
}

/**
 * Federated search sources for the Search page. Order matters: on
 * equal-timestamp ties the stable sort keeps concat order — sessions, notes,
 * then guides.
 */
export const searchSources = [
  {
    type: 'session',
    label: 'sessions',
    chip: null, // sessions are the home type — no card chip
    async load(ctx) {
      const response = await API.getSessionNotes({ limit: 100 });
      const notes = assertDataArray(response, 'sessions');
      // Continuation-title stamping must run on the FULL raw list before
      // normalize so cards and the search index consume the same resolved
      // anchor titles (never resolve against a filtered array).
      ctx?.stampContinuationTitles?.(notes);
      return notes;
    },
    normalize(notes) {
      return notes.map(normalizeSessionNote);
    }
  },
  {
    type: 'note',
    label: 'notes',
    chip: 'Note',
    async load() {
      // Couple gate read inside load() (not module init) so a mid-session
      // partner link is picked up on the next load. Uncoupled users resolve
      // to an empty source silently — not a failure.
      if (!window.app?.currentUser?.coupleId) {
        return { received: [], sent: [], partner: null };
      }
      const [receivedRes, sentRes] = await Promise.all([
        discoveryApi.getMemos(),
        discoveryApi.getSentMemos()
      ]);
      const received = assertDataArray(receivedRes, 'notes');
      const sent = assertDataArray(sentRes, 'notes');
      // Memo payloads carry no partner identity — fetch the partner for card
      // titles. Non-fatal: a failure leaves the 'your partner' fallback.
      let partner = null;
      try {
        const partnerRes = await discoveryApi.getPartner();
        partner = partnerRes?.data || null;
      } catch (err) {
        console.error('[searchSources] Failed to load partner:', err?.error || err);
      }
      return { received, sent, partner };
    },
    normalize({ received, sent, partner }) {
      const partnerName = partner?.firstName
        || partner?.name
        || partner?.username
        || 'your partner';
      return [
        ...received.map(memo => normalizeMemo(memo, 'received', partnerName)),
        // Keep withdrawn sent notes in the federated stream so /search and
        // /notes expose the same lifecycle state.
        ...sent.map(memo => normalizeMemo(memo, 'sent', partnerName))
      ];
    }
  },
  {
    type: 'guide',
    label: 'guides',
    chip: 'Guide',
    async load() {
      // Sourced from getAvailableGuides() (matches every other guide-list UI
      // in the app) — NOT a raw GET /guides list call, whose rows have no
      // cardContent and whose .id is the row UUID, not the guideDefinitionId.
      const response = await API.getAvailableGuides();
      const guides = assertDataArray(response, 'guides');
      return guides.filter(g => g.status === 'READY');
    },
    normalize(guides) {
      return guides.map(normalizeGuide);
    }
  }
];
